import Vue from "vue";
import { copy } from "@/lib/objects";
import { roundPayout } from "@/lib/numbers";
import { isMarketplacePublisher } from "@/utils/offerConnectedPublishers";

function parsePlacementDefaults(raw) {
	if (!raw) {
		return {};
	}

	if (typeof raw === "string") {
		try {
			return JSON.parse(raw) || {};
		} catch (e) {
			return {};
		}
	}

	return raw;
}

function applyPlacementBooleans(placement) {
	Vue.ovData.placement._booleans.forEach((f) => {
		placement[f] = !!placement[f];
	});
}

function getFixedOfferPayoutEvents(offer) {
	return (offer.events || []).filter(
		(event) => event.payout_mode === "fixed" && Number(event.payout) > 0,
	);
}

function hasExplicitFixedPlacementPayout(defaultParams, payoutType) {
	return (
		defaultParams.payout_mode === "fixed" &&
		defaultParams.payout_type === payoutType &&
		Number(defaultParams.payout || 0) > 0
	);
}

function getMarketplacePayoutType(offer, publisher) {
	if (publisher.media_type === "rewarded" || publisher.publisher_media_type === "rewarded") {
		return "CPI";
	}

	return offer.payout_type || null;
}

function resolveMarketplaceGeneratedPlacementPayout(offer, publisher, defaultParams = {}) {
	const payoutType = getMarketplacePayoutType(offer, publisher);
	const payout = Number(defaultParams.payout || 0);
	const payableEvents =
		payoutType === "CPA" && defaultParams.payout_mode === "fixed"
			? getFixedOfferPayoutEvents(offer).map((event) => ({
				name: event.name,
				payout,
			}))
			: [];

	return {
		payout_mode: defaultParams.payout_mode || "fixed",
		payout_type: payoutType,
		payout,
		payable_events: payableEvents,
	};
}

function getGeneratedPlacementPayoutPercent(publisher, options = {}) {
	const publisherPercent = Number(
		publisher.default_placement_payout || publisher.publisher_default_placement_payout || 0,
	);
	if (publisherPercent > 0) {
		return publisherPercent;
	}

	return options.defaultPlacementPayoutPercent || 0;
}

function resolveGeneratedPlacementPayout(offer, publisher, defaultParams = {}, options = {}) {
	if (isMarketplacePublisher(publisher)) {
		return resolveMarketplaceGeneratedPlacementPayout(offer, publisher, defaultParams);
	}

	const payoutType = defaultParams.payout_type || offer.payout_type || null;
	const payoutMode = defaultParams.payout_mode || "fixed";
	const payoutPercent = getGeneratedPlacementPayoutPercent(publisher, options);
	const fixedOfferEvents = getFixedOfferPayoutEvents(offer);
	const hasOfferPayout =
		payoutType === "CPA"
			? fixedOfferEvents.length > 0
			: (Number(offer.payout || 0) > 0 && !offer.dynamic_payout) ||
				hasExplicitFixedPlacementPayout(defaultParams, payoutType);

	if (!hasOfferPayout) {
		return {
			payout_mode: "fixed",
			payout_type: payoutType,
			payout: 0,
			payable_events: [],
		};
	}

	if (payoutMode === "dynamic") {
		return {
			payout_mode: payoutMode,
			payout_type: payoutType,
			payout: defaultParams.payout,
			payable_events: [],
		};
	}

	if (payoutType === "CPA") {
		const payableEvents = fixedOfferEvents.map((event) => ({
			name: event.name,
			payout: roundPayout(Number(event.payout) * (payoutPercent / 100)),
		}));

		return {
			payout_mode: payoutMode,
			payout_type: payoutType,
			payout: Math.max(...payableEvents.map((event) => event.payout)),
			payable_events: payableEvents,
		};
	}

	const payout = hasExplicitFixedPlacementPayout(defaultParams, payoutType)
		? defaultParams.payout
		: roundPayout(Number(offer.payout || 0) * (payoutPercent / 100));

	return {
		payout_mode: payoutMode,
		payout_type: payoutType,
		payout,
		payable_events: [],
	};
}

async function getPublisherOrGlobalPlacementDefaults(publisher) {
	const pubDefaults = parsePlacementDefaults(publisher.publisher_placement_defaults);
	if (Object.keys(pubDefaults).length) {
		return pubDefaults;
	}

	try {
		return await Vue.ovData.placement.getDefaults(false) || {};
	} catch (e) {
		console.error(e);
		return { payout_mode: "fixed" };
	}
}

function buildPublisherPlacementPayoutDraft(offer, publisher, defaultParams) {
	const resolved = resolveGeneratedPlacementPayout(
		offer,
		{
			publisher_type: publisher.publisher_type,
			media_type: publisher.publisher_media_type,
			default_placement_payout:
				publisher.publisher_default_placement_payout || publisher.default_placement_payout,
		},
		defaultParams,
	);

	if (isMarketplacePublisher(publisher)) {
		return {
			...resolved,
			payout: Number(resolved.payout),
			payout_type: resolved.payout_type || offer.payout_type || null,
			payout_mode: resolved.payout_mode || "fixed",
			payable_events: copy(resolved.payable_events || []),
			currency: publisher.currency || offer.currency || "USD",
			daily_conversion_cap: Number(
				defaultParams.daily_conversion_cap || publisher.daily_conversion_cap || 0,
			),
		};
	}

	return {
		...resolved,
		currency: offer.currency || "USD",
	};
}

// Returns true if payout model must be manually selected
function requireExplicitPayoutSelection(defaultParams, offer) {
	if (offer.payout_type !== "CPM") {
		return false;
	}

	// -- for CPM, we need to manually select the payout model --

	// case 1: 'Auto' on PlacementDefaults (fixed mode only)
	if (defaultParams.payout_mode === "fixed" && defaultParams.payout_type == null) {
		return true;
	}

	// case 2: 'Dynamic' on PlacementDefaults
	return defaultParams.payout_mode === "dynamic";
}

function isMediaBuyingPublisher(publisher) {
	return publisher.publisher_media_type === "media_buying";
}

/**
 * @param {object} offer
 * @param {object} publisher
 */
export async function buildPublisherPlacementForConnect(offer, publisher) {
	const defaultPlacementPayout = Number(getPublisherDefaultPlacementPayout(publisher));
	const defaultParams = await getPublisherOrGlobalPlacementDefaults(publisher);
	const requiresPayoutModelSelection = requireExplicitPayoutSelection(defaultParams, offer)
		&& !isMediaBuyingPublisher(publisher)
		&& !isMarketplacePublisher(publisher);

	let placement = await Vue.ovData.placement.getDefaults(true);
	applyPlacementBooleans(placement);
	Object.assign(placement, defaultParams);
	applyPlacementBooleans(placement);

	const payoutDraft = buildPublisherPlacementPayoutDraft(offer, publisher, defaultParams);

	placement.payout = payoutDraft.payout;
	placement.payout_mode = payoutDraft.payout_mode || placement.payout_mode || "fixed";
	placement.payout_type = payoutDraft.payout_type ?? placement.payout_type;
	placement.currency = payoutDraft.currency || offer.currency || placement.currency || "USD";
	placement.payable_events = copy(payoutDraft.payable_events || []);

	if (payoutDraft.daily_conversion_cap != null) {
		placement.daily_conversion_cap = payoutDraft.daily_conversion_cap;
	}

	if (requiresPayoutModelSelection) {
		placement.payout_mode = undefined;
		placement.payout_type = undefined;
	}

	if (isMediaBuyingPublisher(publisher)) {
		placement.payout_mode = "fixed";
		placement.payout_type = "CPM";
		placement.payout = 0;
		placement.payable_events = [];
	}

	return {
		placement,
		defaultPlacementPayout,
	};
}

export function calcPlacementEventPayoutFromOffer(offerEventPayout, defaultPlacementPayoutPercent) {
	const payout = Number(offerEventPayout || 0);
	const percent = Number(defaultPlacementPayoutPercent || 0);
	if (!payout || !percent) {
		return payout;
	}

	return roundPayout(payout * (percent / 100));
}

export function buildPayableEventOnSelect(offer, placement, selectedOption, defaultPlacementPayoutPercent) {
	const offerEvent = (offer.events || []).find((e) => e.name === selectedOption.name);
	let payout = Number(offerEvent?.payout ?? selectedOption.payout ?? 0);
	if (
		placement.payout_mode === "fixed" &&
		offerEvent?.payout_mode === "fixed" &&
		payout > 0
	) {
		payout = calcPlacementEventPayoutFromOffer(payout, defaultPlacementPayoutPercent);
	}

	return {
		name: selectedOption.name,
		payout,
	};
}

export function normalizePayableEventPayoutInput(payout) {
	const parsed = Number.parseFloat(payout);
	return Number.isFinite(parsed) ? parsed : "";
}

export function syncPlacementPayoutFromPayableEvents(placement) {
	if (!placement.payable_events?.length) {
		return;
	}

	if (placement.payout_type === "CPA") {
		placement.payout = Math.max(
			...placement.payable_events.map((e) => Number(e.payout) || 0),
		);
	}
}

export function getOfferEventPayout(offer, event) {
	const offerEvent = (offer.events || []).find((e) => e.name === event.name);
	return offerEvent ? offerEvent.payout : 0;
}

export function getPublisherDefaultPlacementPayout(publisher) {
	if (!publisher) {
		return undefined;
	}

	return publisher.publisher_default_placement_payout ?? publisher.default_placement_payout;
}

export function checkCpaPayoutModelAvailable(offer) {
	return (offer?.events || []).length > 0;
}

export function placementToConnectOverrides(placement) {
	return {
		payout: Number(placement.payout),
		payout_mode: placement.payout_mode,
		payout_type: placement.payout_type,
		currency: placement.currency,
		payable_events: (placement.payable_events || []).map((e) => ({
			name: e.name,
			payout: Number(e.payout),
		})),
	};
}
